import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { allNavItems } from '@/lib/nav';
import { searchEntities, type EntityDoc } from '@/lib/entitySearch';
import { useEntityIndex } from '@/lib/hooks/useEntityIndex';
import { cn } from '@/lib/utils';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';

interface PaletteItem {
  id: string;
  label: string;
  sublabel?: string;
  group: string; // 'Go to' | 'Actions' | entity kind
  run: () => void;
}

const MIN_ENTITY_QUERY = 2;

const LISTBOX_ID = 'command-palette-listbox';
const optionId = (id: string) => `command-palette-option-${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

function PaletteBody({ onClose, inputRef, entities, open }: { onClose: () => void; inputRef: React.RefObject<HTMLInputElement | null>; entities: EntityDoc[] | undefined; open: boolean }) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const commands = useMemo<PaletteItem[]>(
    () => [
      { id: 'new-app', label: 'Deploy new app', group: 'Actions', run: () => navigate('/apps/new') },
      { id: 'theme', label: 'Toggle theme', group: 'Actions', run: () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark') },
      { id: 'domains', label: 'Settings → Domains', group: 'Actions', run: () => navigate('/settings/domains') },
      { id: 'subdomains', label: 'Settings → Reserved Subdomains', group: 'Actions', run: () => navigate('/settings/subdomains') },
      { id: 'webhooks', label: 'Settings → Webhooks', group: 'Actions', run: () => navigate('/settings/webhooks') },
      ...allNavItems.map((i) => ({ id: i.path, label: i.label, group: 'Go to' as const, run: () => navigate(i.path) })),
    ],
    [navigate, resolvedTheme, setTheme],
  );

  const trimmed = query.trim();
  const entityQuery = trimmed.length >= MIN_ENTITY_QUERY;
  const searching = open && entities === undefined && entityQuery;

  const results = useMemo<PaletteItem[]>(() => {
    const q = trimmed.toLowerCase();
    const matched = q ? commands.filter((c) => c.label.toLowerCase().includes(q)) : commands;
    if (!entities) return matched;
    const found = searchEntities(entities, trimmed).map<PaletteItem>((d) => ({
      id: `entity:${d.id}`,
      label: d.label,
      sublabel: d.sublabel,
      group: d.kind,
      run: () => navigate(d.to),
    }));
    return [...matched, ...found];
  }, [commands, trimmed, entities, navigate]);

  // Keep the active index valid when the list shrinks (e.g. entities finish loading or change).
  const safeIndex = Math.min(index, Math.max(0, results.length - 1));

  const active = results[safeIndex];

  useEffect(() => {
    if (!active) return;
    document.getElementById(optionId(active.id))?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const run = (c?: PaletteItem) => {
    if (!c) return;
    onClose();
    c.run();
  };

  return (
    <>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded
        aria-controls={LISTBOX_ID}
        aria-activedescendant={active ? optionId(active.id) : undefined}
        aria-autocomplete="list"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIndex(0);
        }}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing || e.keyCode === 229) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIndex(Math.min(safeIndex + 1, Math.max(0, results.length - 1)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIndex(Math.max(safeIndex - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            run(active);
          }
        }}
        placeholder="Jump to a page or run an action…"
        className="h-12 w-full border-b bg-transparent px-4 text-sm outline-none"
        aria-label="Command search"
      />
      <ul ref={listRef} id={LISTBOX_ID} className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label="Results">
        {results.map((c, i) => (
          <li
            key={c.id}
            id={optionId(c.id)}
            role="option"
            aria-selected={i === safeIndex}
            onMouseMove={() => { if (i !== safeIndex) setIndex(i); }}
            onClick={() => run(c)}
            className={cn('flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm', i === safeIndex && 'bg-accent text-accent-foreground')}
          >
            <span className="min-w-0 truncate">
              {c.label}
              {c.sublabel && <span className="ml-2 text-xs text-muted-foreground">{c.sublabel}</span>}
            </span>
            <span className="ml-3 shrink-0 text-xs text-muted-foreground">{c.group}</span>
          </li>
        ))}
      </ul>
      {searching && <p role="status" className="px-3 py-3 text-center text-sm text-muted-foreground">Searching…</p>}
      {results.length === 0 && !searching && <p role="status" className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</p>}
    </>
  );
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const entities = useEntityIndex(open);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-lg"
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          captureOpener();
          e.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={restoreFocus}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        {/* Body is mounted only while open, so query/selection reset on every open. */}
        <PaletteBody onClose={() => onOpenChange(false)} inputRef={inputRef} entities={entities} open={open} />
      </DialogContent>
    </Dialog>
  );
}
